/**
 * StudioCalendarTab — the "Calendar" tab of the Studio workspace (Staff PTO + Studio Holidays +
 * Board Meetings). Moved off its own standalone `?ws=studio-calendar` workspace into Studio
 * (Matt, 2026-08) — Calendar is the one tab visible to every seat type; Org Chart, Board,
 * Handbook, and Mission & Values are admin-only (see StudioWorkspace.jsx).
 *
 * Pure content component — no active/open-close state, no header, no URL sync, no seat-ready
 * polling of its own; the container (StudioWorkspace) owns all of that and passes `events` +
 * `admin` down, matching RoleProfilesTab/BoardTab. Board Meeting events (event_type
 * 'board_meeting', mirrored from api/board-meetings.js) render read-only here — editing one means
 * editing the meeting itself, on the Board tab.
 *
 * No module gate — every signed-in team member can view the calendar and log/edit/delete their
 * own or anyone's PTO (mirrors how the Airtable table had no ownership enforcement either).
 * Studio Holiday management (add/edit/delete the firm-wide closures) is restricted to
 * hasModule('admin') both here and server-side. See docs/STUDIO_CALENDAR.md "Decisions".
 */

import { useState } from 'react'
import { alertDialog, confirmDialog } from './Dialog.jsx'
import AddCalendarEventModal from './AddCalendarEventModal.jsx'
import { ActionMenu, ActionMenuButton, ActionMenuItem, useActionMenu } from './ActionMenu.jsx'
import { listStudioCalendarEvents, deletePto, deleteHoliday } from '../services/studioCalendar.js'
import { ctx } from '../outline/ctx.js'

const VIEW_KEY = 'aj:studio-cal:view'

const PALETTE = ['#1D6FB8', '#1D9E75', '#b8860b', '#c0392b', '#7a5aa8', '#2f7a7a', '#a8548f', '#5a7a2f']
function colorFor(id) {
  let h = 0
  for (let i = 0; i < (id || '').length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return PALETTE[h % PALETTE.length]
}

function initials(name) {
  return (name || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()
}

function fmtDate(iso) {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
function fmtRange(start, end) {
  if (start === end) return fmtDate(start)
  const a = new Date(start + 'T00:00:00'), b = new Date(end + 'T00:00:00')
  if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
    const month = a.toLocaleDateString(undefined, { month: 'short' })
    return `${month} ${a.getDate()}–${b.getDate()}, ${a.getFullYear()}`
  }
  return fmtDate(start) + ' – ' + fmtDate(end)
}
function dayCount(start, end) {
  return Math.round((new Date(end + 'T00:00:00') - new Date(start + 'T00:00:00')) / 86400000) + 1
}
function isoDay(d) { return d.toISOString().slice(0, 10) }

/** roles: array | null. admin: bool. onEventsChange(nextArray). */
export default function StudioCalendarTab({ events, admin, onEventsChange }) {
  const [view, setView] = useState(() => { try { return localStorage.getItem(VIEW_KEY) || 'month' } catch { return 'month' } })
  const [month, setMonth] = useState(() => { const d = new Date(); d.setDate(1); return d })
  const [editing, setEditing] = useState(null) // { kind, event } | null
  const currentMember = ctx.currentMember ? { id: ctx.currentMember.id, name: (ctx.currentMember.fields || {})['Member Name'] || '' } : null

  function setViewPersist(v) { setView(v); try { localStorage.setItem(VIEW_KEY, v) } catch { /* ignore */ } }

  async function reload() {
    try { onEventsChange(await listStudioCalendarEvents()) }
    catch (e) { onEventsChange([]); alertDialog({ title: 'Could not load', message: e.message || 'Failed to load the studio calendar.' }) }
  }

  async function onDelete(ev) {
    const label = ev.event_type === 'holiday' ? ev.holiday_name : (ev.member_name || 'this time off')
    if (!(await confirmDialog({ title: 'Delete?', message: `Delete "${label}" (${fmtRange(ev.start_date, ev.end_date)})? This cannot be undone.`, confirmLabel: 'Delete', danger: true }))) return
    try {
      if (ev.event_type === 'holiday') await deleteHoliday(ev.id)
      else await deletePto(ev.id)
      onEventsChange((events || []).filter(e => e.id !== ev.id))
    } catch (e) {
      alertDialog({ title: 'Delete failed', message: e.message })
    }
  }

  return (
    <div style={S.panel}>
      <div style={S.toolbar}>
        <div style={S.tabsWrap}>
          <button style={S.tabBtn(view === 'month')} onClick={() => setViewPersist('month')}>Month</button>
          <button style={S.tabBtn(view === 'list')} onClick={() => setViewPersist('list')}>List</button>
        </div>
        <div style={{ flex: 1 }} />
        {admin && <button style={S.ghostBtn} onClick={() => setEditing({ kind: 'holiday', event: null })}>+ Add studio holiday</button>}
        <button style={S.primaryBtn} onClick={() => setEditing({ kind: 'pto', event: null })} disabled={!currentMember && !admin}>+ Log time off</button>
      </div>

      <div style={S.content}>
        {events === null ? (
          <div style={S.loading}>Loading…</div>
        ) : view === 'month' ? (
          <MonthView
            events={events} month={month} setMonth={setMonth}
            onEditPto={ev => setEditing({ kind: 'pto', event: ev })}
            onEditHoliday={ev => setEditing({ kind: 'holiday', event: ev })}
            admin={admin}
          />
        ) : (
          <ListView events={events} onEditPto={ev => setEditing({ kind: 'pto', event: ev })} onEditHoliday={ev => setEditing({ kind: 'holiday', event: ev })} onDelete={onDelete} admin={admin} />
        )}
      </div>

      {editing && (
        <AddCalendarEventModal
          kind={editing.kind} event={editing.event} isAdmin={admin} currentMember={currentMember}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload() }}
        />
      )}
    </div>
  )
}

/* ── Month view ────────────────────────────────────────────────────────────── */

function MonthView({ events, month, setMonth, onEditPto, onEditHoliday, admin }) {
  const year = month.getFullYear(), mo = month.getMonth()
  const first = new Date(year, mo, 1)
  const gridStart = new Date(first); gridStart.setDate(first.getDate() - first.getDay())
  const days = Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d })
  const todayIso = isoDay(new Date())

  const byDay = {} // iso -> { holidays: [ev], pto: [ev], meetings: [ev] }
  for (const ev of events) {
    let d = new Date(ev.start_date + 'T00:00:00')
    const last = new Date(ev.end_date + 'T00:00:00')
    while (d <= last) {
      const iso = isoDay(d)
      byDay[iso] = byDay[iso] || { holidays: [], pto: [], meetings: [] }
      byDay[iso][ev.event_type === 'holiday' ? 'holidays' : ev.event_type === 'board_meeting' ? 'meetings' : 'pto'].push(ev)
      d = new Date(d.getTime() + 86400000)
    }
  }

  return (
    <div style={{ padding: '12px 18px 40px' }}>
      <div style={S.monthBar}>
        <button style={S.navBtn} onClick={() => setMonth(new Date(year, mo - 1, 1))}>‹</button>
        <span style={S.monthLabel}>{first.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</span>
        <button style={S.navBtn} onClick={() => setMonth(new Date(year, mo + 1, 1))}>›</button>
        <button style={S.todayBtn} onClick={() => { const d = new Date(); d.setDate(1); setMonth(d) }}>Today</button>
      </div>
      <div style={S.grid}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(w => <div key={w} style={S.gridHead}>{w}</div>)}
        {days.map(d => {
          const iso = isoDay(d)
          const info = byDay[iso]
          const inMonth = d.getMonth() === mo
          return (
            <div key={iso} style={{ ...S.cell, ...(inMonth ? {} : S.cellOut), ...(iso === todayIso ? S.cellToday : {}) }}>
              <div style={S.cellDate}>{d.getDate()}</div>
              {info && info.holidays.map(h => (
                <div key={h.id} style={S.holidayBanner} title={h.holiday_name} onClick={() => admin && onEditHoliday(h)}>{h.holiday_name}</div>
              ))}
              {info && info.meetings.map(m => (
                <div key={m.id} style={S.meetingBanner} title={m.holiday_name}>{m.holiday_name}</div>
              ))}
              {info && info.pto.map(p => (
                <div key={p.id} style={{ ...S.chip, background: colorFor(p.member_airtable_id) }} title={p.member_name} onClick={() => onEditPto(p)}>
                  {initials(p.member_name)}
                </div>
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ── List view ─────────────────────────────────────────────────────────────── */

function ListView({ events, onEditPto, onEditHoliday, onDelete, admin }) {
  const rows = events.slice().sort((a, b) => a.start_date < b.start_date ? -1 : a.start_date > b.start_date ? 1 : 0)
  const groups = new Map()
  for (const ev of rows) {
    const d = new Date(ev.start_date + 'T00:00:00')
    const key = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(ev)
  }

  if (!rows.length) return <div style={S.empty}>No time off, studio holidays, or board meetings logged yet.</div>

  return (
    <div style={{ padding: '4px 18px 40px' }}>
      {[...groups.entries()].map(([label, rows]) => (
        <div key={label}>
          <div style={S.groupHead}>{label}</div>
          {rows.map(ev => <Row key={ev.id} ev={ev} onEditPto={onEditPto} onEditHoliday={onEditHoliday} onDelete={onDelete} admin={admin} />)}
        </div>
      ))}
    </div>
  )
}

function Row({ ev, onEditPto, onEditHoliday, onDelete, admin }) {
  const menu = useActionMenu()
  const isHoliday = ev.event_type === 'holiday'
  const isMeeting = ev.event_type === 'board_meeting'
  const canEdit = isMeeting ? false : isHoliday ? admin : true
  return (
    <div style={S.row}>
      {isMeeting
        ? <span style={{ ...S.rowDot, background: 'var(--aj-accent-blue)' }} />
        : isHoliday
          ? <span style={{ ...S.rowDot, background: '#8a6508' }} />
          : <span style={{ ...S.rowDot, background: colorFor(ev.member_airtable_id) }} />}
      <span style={S.rowLabel}>{isHoliday || isMeeting ? ev.holiday_name : ev.member_name}</span>
      <span style={S.rowBadge}>{isHoliday ? 'Studio Holiday' : isMeeting ? 'Board Meeting' : 'PTO'}</span>
      <span style={{ flex: 1 }} />
      <span style={S.rowDates}>{fmtRange(ev.start_date, ev.end_date)}</span>
      <span style={S.rowDuration}>{dayCount(ev.start_date, ev.end_date)}d</span>
      {canEdit && (
        <>
          <ActionMenuButton menu={menu} />
          <ActionMenu menu={menu}>
            <ActionMenuItem onClick={() => { menu.close(); isHoliday ? onEditHoliday(ev) : onEditPto(ev) }}>Edit</ActionMenuItem>
            <ActionMenuItem onClick={() => { menu.close(); onDelete(ev) }} danger>Delete</ActionMenuItem>
          </ActionMenu>
        </>
      )}
    </div>
  )
}

const S = {
  panel: { display: 'flex', flexDirection: 'column', height: '100%' },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderBottom: '0.5px solid var(--aj-border)' },
  tabsWrap: { display: 'flex', gap: 2, background: 'var(--aj-bg-muted)', borderRadius: 7, padding: 2 },
  tabBtn: on => on
    ? { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, fontWeight: 600, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)', border: 'none', borderRadius: 5, padding: '4px 12px', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.12)' }
    : { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', background: 'none', border: 'none', borderRadius: 5, padding: '4px 12px', cursor: 'pointer' },
  content: { flex: 1, overflow: 'auto', background: 'var(--aj-bg-page)' },
  loading: { padding: 40, textAlign: 'center', color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif', fontSize: 13 },
  empty: { padding: 40, textAlign: 'center', color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif', fontSize: 13 },
  primaryBtn: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer' },
  ghostBtn: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, fontWeight: 600, color: 'var(--aj-text)', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', marginRight: 8 },

  monthBar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  navBtn: { background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, width: 26, height: 26, cursor: 'pointer', color: 'var(--aj-text)', fontSize: 14 },
  monthLabel: { fontFamily: 'Montserrat,sans-serif', fontWeight: 600, fontSize: 13, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--aj-text)', minWidth: 160 },
  todayBtn: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '4px 10px', cursor: 'pointer' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1, background: 'var(--aj-border)', border: '0.5px solid var(--aj-border)', borderRadius: 6, overflow: 'hidden' },
  gridHead: { padding: '6px 8px', fontFamily: '"DM Mono",monospace', fontSize: 9.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', background: 'var(--aj-bg-subtle)' },
  cell: { minHeight: 88, background: 'var(--aj-bg-elevated)', padding: '4px 5px', display: 'flex', flexDirection: 'column', gap: 3, overflow: 'hidden' },
  cellOut: { background: 'var(--aj-bg-page)', opacity: 0.55 },
  cellToday: { boxShadow: 'inset 0 0 0 1.5px var(--aj-accent)' },
  cellDate: { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-mid)' },
  holidayBanner: { fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, fontWeight: 600, color: '#8a6508', background: 'rgba(184,134,11,0.14)', borderRadius: 3, padding: '1px 5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' },
  meetingBanner: { fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, fontWeight: 600, color: 'var(--aj-accent-blue)', background: 'rgba(29,111,184,0.12)', borderRadius: 3, padding: '1px 5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' },
  chip: { fontFamily: '"DM Mono",monospace', fontSize: 9.5, fontWeight: 600, color: '#fff', borderRadius: 3, padding: '1px 5px', width: 'fit-content', cursor: 'pointer' },

  groupHead: { padding: '14px 0 6px', fontFamily: '"Montserrat",sans-serif', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text)', fontWeight: 600 },
  row: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '0.5px solid var(--aj-border)' },
  rowDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  rowLabel: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' },
  rowBadge: { fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: 'var(--aj-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  rowDates: { fontFamily: '"DM Mono",monospace', fontSize: 12, color: 'var(--aj-text-mid)' },
  rowDuration: { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)', minWidth: 24, textAlign: 'right' },
}
